import { useState, useEffect } from "react";
import {
  X,
  Clock,
  Calendar,
  User,
  Users,
  HardDrive,
  Loader2,
  Play,
  Trash2,
  Eye,
  Plus,
  Trash,
  AlertTriangle,
  Archive,
  RefreshCw,
  Download,
  CheckCircle2,
  Circle,
} from "lucide-react";
import { colors, fonts } from "../../styles/theme";
import { Button } from "../../components/ui";
import { StatusBadge, PlatformBadge } from "./StatusBadge";
import TranscriptViewer from "./TranscriptViewer";
import ClipPlayer from "./ClipPlayer";
import MeetingSummary from "./MeetingSummary";
import {
  fetchMeeting,
  processMeeting,
  redownloadMeeting,
  deleteMeetingRecord,
  deleteMeetingClip,
  regenerateMeetingClips,
  summarizeMeeting,
  updateMeeting,
  ingestZoomClip,
} from "../../services/api";

function friendlyError(msg) {
  if (!msg) return "An unknown error occurred.";
  if (/timed out|exceeded 2 hours/i.test(msg)) return "Transcription timed out. Try re-processing — if the issue persists, the recording may be too long or corrupted.";
  if (/stalled.*never submitted/i.test(msg)) return "Processing stalled before transcription could start. Click Re-process to try again.";
  if (/download failed|AZURE_STORAGE/i.test(msg)) return "Failed to download or store the recording. Click Re-process to try again.";
  if (/speech_models/i.test(msg)) return "Transcription service configuration error. This has been reported — please try again later.";
  if (/Zoom download failed/i.test(msg)) return "Could not download from Zoom. The recording may have expired or been deleted.";
  if (/content_filter|jailbreak/i.test(msg)) return "AI summary was blocked by Azure content filters. An admin may need to adjust the content filter policy in Azure AI Foundry.";
  if (/Azure OpenAI not configured/i.test(msg)) return "AI services are not configured. Contact your administrator to set up Azure OpenAI.";
  if (/AssemblyAI/i.test(msg)) return "Transcription service error (AssemblyAI). Click Re-process to try again — if the issue persists, their service may be experiencing issues.";
  if (/too large|2MB|document size/i.test(msg)) return "The transcript is too large to store. The recording may need to be split into shorter segments.";
  return msg;
}

export default function MeetingDrawer({
  meeting,
  onClose,
  onUpdated,
  onDeleted,
  userEmail,
  isAdmin,
}) {
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("details");
  const [processing, setProcessing] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [summarizing, setSummarizing] = useState(false);
  const [extending, setExtending] = useState(false);
  const [redownloading, setRedownloading] = useState(false);

  useEffect(() => {
    if (!meeting) return;
    setLoading(true);
    setTab("details");
    fetchMeeting(meeting.id)
      .then((data) => setDetail(data))
      .catch(() => setDetail(meeting))
      .finally(() => setLoading(false));
  }, [meeting]);

  // Auto-poll while processing/downloading — picks up status changes
  useEffect(() => {
    if (!detail || (detail.status !== "processing" && detail.status !== "pending-download")) return;
    const id = setInterval(() => {
      fetchMeeting(detail.id)
        .then((fresh) => {
          if (fresh.status !== detail.status) {
            setDetail(fresh);
            onUpdated?.(fresh);
          }
        })
        .catch(() => {});
    }, 10_000);
    return () => clearInterval(id);
  }, [detail?.id, detail?.status]);

  // Elapsed-time ticker for processing display
  const [elapsed, setElapsed] = useState(0);
  useEffect(() => {
    const startField = detail?._transcriptionSubmittedAt || (detail?.status === "processing" ? detail?.updatedAt : null);
    if (!detail || detail.status !== "processing" || !startField) {
      setElapsed(0);
      return;
    }
    const startedAt = new Date(startField).getTime();
    setElapsed(Math.max(0, (Date.now() - startedAt) / 1000));
    const id = setInterval(() => {
      setElapsed(Math.max(0, (Date.now() - startedAt) / 1000));
    }, 1000);
    return () => clearInterval(id);
  }, [detail?.id, detail?.status, detail?._transcriptionSubmittedAt, detail?.updatedAt]);

  if (!meeting) return null;

  const m = detail || meeting;

  const formatDuration = (seconds) => {
    if (!seconds) return "—";
    const h = Math.floor(seconds / 3600);
    const min = Math.floor((seconds % 3600) / 60);
    if (h > 0) return `${h}h ${min}m`;
    return `${min}m`;
  };

  const formatFileSize = (bytes) => {
    if (!bytes) return "—";
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
    if (bytes < 1024 * 1024 * 1024)
      return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
    return `${(bytes / (1024 * 1024 * 1024)).toFixed(2)} GB`;
  };

  const handleProcess = async () => {
    setProcessing(true);
    try {
      // For zoom-clip meetings without a blob, re-trigger through ingest flow
      if (m.platform === "zoom-clip" && !m.blobName && m.sourceClipId) {
        const result = await ingestZoomClip(m.sourceClipId, m.title);
        if (result.triggerWarning) {
          alert(`Pipeline trigger warning: ${result.triggerWarning}`);
        }
        setDetail({ ...m, status: "pending-download" });
        onUpdated?.({ ...m, status: "pending-download" });
      } else {
        const updated = await processMeeting(m.id);
        setDetail(updated);
        onUpdated?.(updated);
      }
    } catch (err) {
      alert(err.message || "Processing failed");
    } finally {
      setProcessing(false);
    }
  };

  const handleRedownload = async () => {
    if (
      !window.confirm(
        "Re-download the recording from Zoom? This will replace the current file and reset to 'uploaded' status.",
      )
    )
      return;
    setRedownloading(true);
    try {
      const updated = await redownloadMeeting(m.id);
      setDetail(updated);
      onUpdated?.(updated);
    } catch (err) {
      alert(err.message || "Failed to re-download recording.");
    } finally {
      setRedownloading(false);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm(`Delete "${m.title}"? This cannot be undone.`)) return;
    setDeleting(true);
    try {
      await deleteMeetingRecord(m.id);
      onDeleted?.(m.id);
    } catch {
      setDeleting(false);
    }
  };

  const handleSummarize = async () => {
    setSummarizing(true);
    try {
      const result = await summarizeMeeting(m.id);
      if (result.triggerWarning) {
        alert(`Summary trigger warning: ${result.triggerWarning}`);
        setSummarizing(false);
        return;
      }
      // Poll for completion — summary is generated async by meeting-functions
      const poll = setInterval(async () => {
        try {
          const fresh = await fetchMeeting(m.id);
          if (fresh.summary) {
            clearInterval(poll);
            setDetail(fresh);
            onUpdated?.(fresh);
            setSummarizing(false);
          }
        } catch { /* retry next tick */ }
      }, 5000);
      // Stop polling after 2 minutes
      setTimeout(() => { clearInterval(poll); setSummarizing(false); }, 120_000);
    } catch (err) {
      alert(err.message || "Summary generation failed");
      setSummarizing(false);
    }
  };

  const handleExtendRetention = async () => {
    setExtending(true);
    try {
      const newExpiry = new Date(
        Date.now() + 90 * 24 * 60 * 60 * 1000,
      ).toISOString();
      const updated = await updateMeeting(m.id, {
        retentionExpiresAt: newExpiry,
        retentionWarning: false,
      });
      setDetail(updated);
      onUpdated?.(updated);
    } catch {
      // silently fail
    } finally {
      setExtending(false);
    }
  };

  const canManageViewers =
    isAdmin ||
    (m.uploadedBy &&
      userEmail &&
      m.uploadedBy.toLowerCase() === userEmail.toLowerCase());

  const tabs = [
    { id: "details", label: "Details" },
    { id: "summary", label: "Summary" },
    { id: "transcript", label: "Transcript" },
    { id: "clips", label: "Clips", count: m.clips?.length || 0 },
    { id: "contacts", label: "Contacts", count: m.linkedContacts?.length || 0 },
  ];

  return (
    <>
      <div
        onClick={onClose}
        style={{
          position: "fixed",
          inset: 0,
          background: "rgba(0,0,0,0.4)",
          zIndex: 99,
        }}
      />
      <div
        style={{
          position: "fixed",
          top: 0,
          right: 0,
          bottom: 0,
          width: 520,
          background: colors.bgCard,
          borderLeft: `1px solid ${colors.border}`,
          zIndex: 100,
          display: "flex",
          flexDirection: "column",
          boxShadow: "-20px 0 60px rgba(0,0,0,0.12)",
        }}
      >
        {/* Header */}
        <div
          style={{
            padding: "24px 28px",
            borderBottom: `1px solid ${colors.border}`,
          }}
        >
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "flex-start",
              marginBottom: 12,
            }}
          >
            <div style={{ flex: 1, minWidth: 0 }}>
              <h2
                style={{
                  fontFamily: fonts.heading,
                  fontSize: 20,
                  color: colors.text,
                  margin: 0,
                  marginBottom: 8,
                }}
              >
                {m.title}
              </h2>
              <div style={{ display: "flex", gap: 8 }}>
                <PlatformBadge platform={m.platform} />
                <StatusBadge status={m.status} />
              </div>
            </div>
            <X
              size={20}
              color={colors.textSecondary}
              style={{ cursor: "pointer", flexShrink: 0 }}
              onClick={onClose}
            />
          </div>

          <div
            style={{
              display: "flex",
              gap: 16,
              fontSize: 13,
              color: colors.textSecondary,
              marginTop: 12,
            }}
          >
            <span style={{ display: "flex", alignItems: "center", gap: 4 }}>
              <Calendar size={13} />
              {new Date(m.recordedAt).toLocaleDateString("en-GB", {
                day: "numeric",
                month: "short",
                year: "numeric",
              })}
            </span>
            <span style={{ display: "flex", alignItems: "center", gap: 4 }}>
              <Clock size={13} />
              {formatDuration(m.durationSeconds)}
            </span>
          </div>

          <div style={{ display: "flex", gap: 8, marginTop: 14 }}>
            {(m.status === "uploaded" || m.status === "pending-download") && (
              <Button
                variant="primary"
                size="sm"
                icon={Play}
                onClick={handleProcess}
              >
                {processing ? "Processing..." : "Process Recording"}
              </Button>
            )}
            {(m.status === "processing" || m.status === "error") && (
              <Button
                variant="primary"
                size="sm"
                icon={RefreshCw}
                onClick={handleProcess}
              >
                {processing ? "Re-processing..." : "Re-process"}
              </Button>
            )}
            {m.zoomMeetingId && m.status !== "archived" && (
              <Button
                variant="secondary"
                size="sm"
                icon={Download}
                onClick={handleRedownload}
                disabled={redownloading}
              >
                {redownloading ? "Downloading..." : "Re-download"}
              </Button>
            )}
            {m.status !== "archived" && (
              <Button
                variant="danger"
                size="sm"
                icon={Trash2}
                onClick={handleDelete}
              >
                {deleting ? "Deleting..." : "Delete"}
              </Button>
            )}
          </div>

          {/* Error message */}
          {m.status === "error" && m.errorMessage && (
            <div
              style={{
                display: "flex",
                alignItems: "flex-start",
                gap: 8,
                marginTop: 12,
                padding: "10px 14px",
                borderRadius: 8,
                background: "rgba(239,68,68,0.08)",
                border: "1px solid rgba(239,68,68,0.2)",
                fontSize: 13,
                color: "#dc2626",
              }}
            >
              <AlertTriangle
                size={14}
                style={{ marginTop: 2, flexShrink: 0 }}
              />
              <span>{friendlyError(m.errorMessage)}</span>
            </div>
          )}

          {/* Pipeline progress indicator — show during processing, and for completed meetings to show what's done */}
          {["processing", "pending-download", "transcribed", "clips-ready", "error"].includes(m.status) && (
            <PipelineSteps meeting={m} elapsed={elapsed} />
          )}

          {/* Archived notice */}
          {m.status === "archived" && (
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: 8,
                marginTop: 12,
                padding: "10px 14px",
                borderRadius: 8,
                background: "rgba(255,255,255,0.04)",
                border: `1px solid ${colors.border}`,
                fontSize: 12,
                color: colors.textSecondary,
              }}
            >
              <Archive size={14} color={colors.textSecondary} />
              <span>Recording deleted — transcript and summary preserved.</span>
            </div>
          )}

          {/* Retention warning */}
          {m.retentionWarning && m.status !== "archived" && (
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: 8,
                marginTop: 12,
                padding: "10px 14px",
                borderRadius: 8,
                background: "rgba(245,158,11,0.08)",
                border: "1px solid rgba(245,158,11,0.25)",
                fontSize: 12,
                color: colors.warning,
              }}
            >
              <AlertTriangle size={14} />
              <span style={{ flex: 1 }}>
                Recording will be deleted on{" "}
                {new Date(m.retentionExpiresAt).toLocaleDateString("en-GB", {
                  day: "numeric",
                  month: "short",
                  year: "numeric",
                })}
                .
              </span>
              <button
                onClick={handleExtendRetention}
                disabled={extending}
                style={{
                  background: "rgba(245,158,11,0.15)",
                  border: "1px solid rgba(245,158,11,0.3)",
                  borderRadius: 6,
                  padding: "4px 10px",
                  fontSize: 11,
                  fontWeight: 600,
                  color: colors.warning,
                  cursor: extending ? "wait" : "pointer",
                  whiteSpace: "nowrap",
                }}
              >
                {extending ? "Extending..." : "Extend 90 days"}
              </button>
            </div>
          )}
        </div>

        {/* Tabs */}
        <div
          style={{
            display: "flex",
            borderBottom: `1px solid ${colors.border}`,
          }}
        >
          {tabs.map((t) => (
            <button
              key={t.id}
              onClick={() => setTab(t.id)}
              style={{
                flex: 1,
                padding: "10px 0",
                background: "none",
                border: "none",
                borderBottom:
                  tab === t.id
                    ? `2px solid ${colors.accent}`
                    : "2px solid transparent",
                color: tab === t.id ? colors.text : colors.textSecondary,
                fontSize: 11,
                fontWeight: 600,
                fontFamily: fonts.body,
                cursor: "pointer",
              }}
            >
              {t.label}
              {t.count != null && t.count > 0 ? ` (${t.count})` : ""}
            </button>
          ))}
        </div>

        {/* Tab content */}
        <div style={{ flex: 1, overflowY: "auto", padding: "16px 28px" }}>
          {loading ? (
            <div
              style={{
                textAlign: "center",
                padding: 32,
                color: colors.textSecondary,
              }}
            >
              <Loader2
                size={20}
                className="spin"
              />
            </div>
          ) : tab === "details" ? (
            <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
              {/* Host */}
              <div>
                <div
                  style={{
                    fontSize: 11,
                    color: colors.textSecondary,
                    textTransform: "uppercase",
                    letterSpacing: "0.05em",
                    marginBottom: 4,
                  }}
                >
                  Host
                </div>
                <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                  <User size={14} color={colors.textSecondary} />
                  <span style={{ fontSize: 13, color: colors.text }}>
                    {m.hostName || "Unknown"}
                    {m.hostEmail ? ` (${m.hostEmail})` : ""}
                  </span>
                </div>
              </div>

              {/* Participants */}
              {m.participants?.length > 0 && (
                <div>
                  <div
                    style={{
                      fontSize: 11,
                      color: colors.textSecondary,
                      textTransform: "uppercase",
                      letterSpacing: "0.05em",
                      marginBottom: 4,
                    }}
                  >
                    Participants ({m.participants.length})
                  </div>
                  {m.participants.map((p, i) => (
                    <div
                      key={i}
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: 8,
                        padding: "4px 0",
                      }}
                    >
                      <Users size={13} color={colors.textSecondary} />
                      <span style={{ fontSize: 13, color: colors.text }}>
                        {p.name}
                        {p.email ? ` (${p.email})` : ""}
                      </span>
                    </div>
                  ))}
                </div>
              )}

              {/* File info */}
              {m.fileSize > 0 && (
                <div>
                  <div
                    style={{
                      fontSize: 11,
                      color: colors.textSecondary,
                      textTransform: "uppercase",
                      letterSpacing: "0.05em",
                      marginBottom: 4,
                    }}
                  >
                    Recording
                  </div>
                  <div
                    style={{ display: "flex", alignItems: "center", gap: 8 }}
                  >
                    <HardDrive size={14} color={colors.textSecondary} />
                    <span style={{ fontSize: 13, color: colors.text }}>
                      {formatFileSize(m.fileSize)}
                    </span>
                  </div>
                  {m.blobUrl && m.status !== "archived" && (
                    <a
                      href={m.blobUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{
                        fontSize: 12,
                        color: colors.info,
                        textDecoration: "none",
                        marginTop: 4,
                        display: "inline-block",
                      }}
                    >
                      Open recording
                    </a>
                  )}
                </div>
              )}

              {/* Uploaded by */}
              {m.uploadedBy && (
                <div>
                  <div
                    style={{
                      fontSize: 11,
                      color: colors.textSecondary,
                      textTransform: "uppercase",
                      letterSpacing: "0.05em",
                      marginBottom: 4,
                    }}
                  >
                    Uploaded By
                  </div>
                  <span style={{ fontSize: 13, color: colors.text }}>
                    {m.uploadedBy}
                  </span>
                </div>
              )}

              {/* Allowed Viewers */}
              {canManageViewers && (
                <ViewersSection
                  viewers={m.allowedViewers || []}
                  meetingId={m.id}
                  onUpdate={(updated) => {
                    setDetail(updated);
                    onUpdated?.(updated);
                  }}
                />
              )}
            </div>
          ) : tab === "summary" ? (
            <MeetingSummary
              summary={m.summary}
              status={m.status}
              onRegenerate={
                (isAdmin || canManageViewers) && m.transcript
                  ? handleSummarize
                  : undefined
              }
              regenerating={summarizing}
            />
          ) : tab === "transcript" ? (
            m.transcript ? (
              <TranscriptViewer transcript={m.transcript} meetingTitle={m.title} />
            ) : (
              <div
                style={{
                  textAlign: "center",
                  padding: 32,
                  color: colors.textSecondary,
                  fontSize: 13,
                }}
              >
                {m.status === "uploaded"
                  ? 'Click "Process Recording" to generate a transcript.'
                  : m.status === "processing"
                    ? "Transcript is being generated..."
                    : "No transcript available."}
              </div>
            )
          ) : tab === "clips" ? (
            <ClipPlayer
              clips={m.clips}
              meetingStatus={m.status}
              onDeleteClip={async (clipId) => {
                await deleteMeetingClip(m.id, clipId);
                const refreshed = await fetchMeeting(m.id);
                setDetail(refreshed);
                onUpdated?.(refreshed);
              }}
              onRegenerateClips={async () => {
                const result = await regenerateMeetingClips(m.id);
                if (result.triggerWarning) {
                  alert(`Clip generation warning: ${result.triggerWarning}`);
                }
                const refreshed = await fetchMeeting(m.id);
                setDetail(refreshed);
                onUpdated?.(refreshed);
              }}
            />
          ) : /* Contacts tab */
          m.linkedContacts?.length > 0 ? (
            m.linkedContacts.map((c, i) => (
              <div
                key={i}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 10,
                  padding: "10px 0",
                  borderBottom: `1px solid ${colors.borderLight}`,
                }}
              >
                <div
                  style={{
                    width: 32,
                    height: 32,
                    borderRadius: "50%",
                    background: "rgba(99,102,241,0.08)",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    color: colors.info,
                    fontWeight: 700,
                    fontSize: 12,
                  }}
                >
                  {(c.name || "?")[0].toUpperCase()}
                </div>
                <div>
                  <div
                    style={{
                      fontSize: 13,
                      fontWeight: 600,
                      color: colors.text,
                    }}
                  >
                    {c.name}
                  </div>
                  {c.email && (
                    <div style={{ fontSize: 12, color: colors.textSecondary }}>
                      {c.email}
                    </div>
                  )}
                </div>
              </div>
            ))
          ) : (
            <div
              style={{
                textAlign: "center",
                padding: 32,
                color: colors.textSecondary,
                fontSize: 13,
              }}
            >
              No linked contacts yet. Contact matching runs automatically after
              transcription.
            </div>
          )}
        </div>      </div>
    </>
  );
}

/* ── Pipeline Steps indicator ─────────────────────────────── */

function PipelineSteps({ meeting: m, elapsed }) {
  // Determine step states from meeting document fields
  const steps = [
    {
      label: "Upload",
      done: !!m.blobUrl,
      active: m.status === "pending-download" || (!m.blobUrl && m.status === "processing"),
      description: m.status === "pending-download"
        ? "Downloading from Zoom..."
        : !m.blobUrl ? "Waiting for upload..." : null,
    },
    {
      label: "Transcribe",
      done: !!m.transcript,
      active: m.status === "processing" && !!m.blobUrl,
      description: m.status === "processing" && m.blobUrl
        ? (m._transcriptionSubmittedAt ? "Transcription in progress..." : "Submitting to transcription...")
        : null,
    },
    {
      label: "Summary",
      done: !!m.summary,
      active: false,
    },
    {
      label: "Clips",
      done: (m.clips?.length || 0) > 0,
      active: false,
    },
  ];

  const elapsedMin = Math.floor(elapsed / 60);
  const elapsedSec = Math.floor(elapsed % 60);
  const activeStep = steps.find((s) => s.active);

  return (
    <div style={{ marginTop: 12 }}>
      {/* Step indicators */}
      <div style={{ display: "flex", alignItems: "center", gap: 0 }}>
        {steps.map((step, i) => (
          <div key={step.label} style={{ display: "flex", alignItems: "center", flex: i < steps.length - 1 ? 1 : "none" }}>
            <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 2 }}>
              {step.done ? (
                <CheckCircle2 size={16} color={colors.success} />
              ) : step.active ? (
                <Loader2
                  size={16}
                  color={colors.warning}
                  className="spin"
                />
              ) : (
                <Circle size={16} color={colors.textSecondary} style={{ opacity: 0.4 }} />
              )}
              <span style={{
                fontSize: 10,
                fontWeight: step.active ? 600 : 500,
                color: step.done ? colors.success : step.active ? colors.warning : colors.textSecondary,
                whiteSpace: "nowrap",
              }}>
                {step.label}
              </span>
            </div>
            {i < steps.length - 1 && (
              <div style={{
                flex: 1,
                height: 2,
                marginInline: 6,
                marginBottom: 14,
                borderRadius: 1,
                background: step.done ? colors.success : "rgba(255,255,255,0.08)",
              }} />
            )}
          </div>
        ))}
      </div>

      {/* Active step description + elapsed time */}
      {activeStep?.description && (
        <div style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginTop: 6,
          fontSize: 11,
          color: colors.textSecondary,
        }}>
          <span>{activeStep.description}</span>
          {elapsed > 0 && (
            <span style={{ color: colors.textSecondary }}>
              {elapsedMin > 0 ? `${elapsedMin}m ${String(elapsedSec).padStart(2, "0")}s` : `${elapsedSec}s`}
            </span>
          )}
        </div>
      )}
    </div>
  );
}

/* ── Manage Viewers section ────────────────────────────────── */

function ViewersSection({ viewers, meetingId, onUpdate }) {
  const [newEmail, setNewEmail] = useState("");
  const [saving, setSaving] = useState(false);

  const addViewer = async () => {
    const email = newEmail.trim().toLowerCase();
    if (!email || viewers.includes(email)) return;
    setSaving(true);
    try {
      const updated = await updateMeeting(meetingId, {
        allowedViewers: [...viewers, email],
      });
      onUpdate(updated);
      setNewEmail("");
    } catch {
      // silently fail
    } finally {
      setSaving(false);
    }
  };

  const removeViewer = async (email) => {
    setSaving(true);
    try {
      const updated = await updateMeeting(meetingId, {
        allowedViewers: viewers.filter((v) => v !== email),
      });
      onUpdate(updated);
    } catch {
      // silently fail
    } finally {
      setSaving(false);
    }
  };

  return (
    <div>
      <div
        style={{
          fontSize: 11,
          color: colors.textSecondary,
          textTransform: "uppercase",
          letterSpacing: "0.05em",
          marginBottom: 6,
          display: "flex",
          alignItems: "center",
          gap: 4,
        }}
      >
        <Eye size={12} /> Allowed Viewers
      </div>

      {viewers.length > 0 ? (
        viewers.map((email) => (
          <div
            key={email}
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              padding: "4px 0",
              fontSize: 13,
              color: colors.text,
            }}
          >
            <span>{email}</span>
            <Trash
              size={12}
              color={colors.textSecondary}
              style={{ cursor: "pointer", flexShrink: 0 }}
              onClick={() => removeViewer(email)}
            />
          </div>
        ))
      ) : (
        <div style={{ fontSize: 12, color: colors.textSecondary, marginBottom: 6 }}>
          No viewers set — all authenticated users can view this video.
        </div>
      )}

      <div
        style={{
          display: "flex",
          gap: 6,
          marginTop: 8,
          alignItems: "center",
        }}
      >
        <input
          type="email"
          value={newEmail}
          onChange={(e) => setNewEmail(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && addViewer()}
          placeholder="Add viewer email..."
          style={{
            flex: 1,
            border: `1px solid ${colors.border}`,
            borderRadius: 6,
            padding: "5px 10px",
            fontSize: 12,
            fontFamily: fonts.body,
            color: colors.text,
            background: colors.bgCard,
            outline: "none",
          }}
        />
        <button
          onClick={addViewer}
          disabled={saving || !newEmail.trim()}
          style={{
            display: "flex",
            alignItems: "center",
            gap: 4,
            padding: "5px 10px",
            background: colors.accent,
            color: "#fff",
            border: "none",
            borderRadius: 6,
            fontSize: 12,
            cursor: saving ? "wait" : "pointer",
            opacity: saving || !newEmail.trim() ? 0.5 : 1,
          }}
        >
          <Plus size={12} /> Add
        </button>
      </div>
    </div>
  );
}
